import axios from 'axios';
import { getIdentity } from './identityService';

// Use empty string fallback for SWA emulator mode (relative paths go through SWA proxy)
const API_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_URL,
});

// Request interceptor to add demo token if present
apiClient.interceptors.request.use(
  (config) => {
    const demoToken = sessionStorage.getItem('demoToken');
    if (demoToken) {
      config.headers['Authorization'] = `Bearer ${demoToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response interceptor for automatic logout on 401 (session timeout/token expiry)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if demo session expired
      const demoSession = sessionStorage.getItem('demoSession');
      if (demoSession) {
        console.warn('Demo session expired (401). Redirecting to login...');
        sessionStorage.removeItem('demoToken');
        sessionStorage.removeItem('demoSession');
        window.location.href = '/';
      } else {
        console.warn('Session expired (401). Redirecting to login...');
        window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
      }
      return new Promise(() => {}); // Never resolves - page is redirecting
    }
    return Promise.reject(error);
  }
);

// Helper to get current authenticated user
const getCurrentUser = async () => {
  try {
    const response = await fetch('/.auth/me');
    if (response.ok) {
      const authData = await response.json();
      const userInfo = authData.clientPrincipal;
      if (userInfo) {
        return {
          id: userInfo.userId,
          email: userInfo.userDetails,
          name: userInfo.userClaims?.find(claim => claim.typ === 'name')?.val || userInfo.userDetails,
          provider: userInfo.identityProvider,
          roles: userInfo.userRoles
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
};

// Authenticated API methods - automatically use current user's email
export const getProfile = async () => {
  const id = await getIdentity();
  if (!id?.user_id) throw new Error('User not authenticated');
  try {
    const response = await apiClient.get(`/api/profile/user/${id.user_id}`);
    return response.data;
  } catch (error) {
    console.error('Error getting profile:', error);
    throw error;
  }
};

export const updateProfile = async (profileData) => {
  const id = await getIdentity();
  if (!id?.user_id) throw new Error('User not authenticated');
  try {
    const response = await apiClient.post(`/api/profile/user/${id.user_id}`, profileData);
    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

export const getLatestReport = async () => {
  const id = await getIdentity();
  if (!id?.user_id) throw new Error('User not authenticated');
  try {
    const response = await apiClient.get(`/api/report/user/${id.user_id}/latest`);
    return response.data;
  } catch (error) {
    console.error('Error getting latest report:', error);
    throw error;
  }
};

// ============================================================================
// TODO: REMOVE - DEAD CODE (Not called from any component)
// ============================================================================
// Function: getReportAnalysis()
//
// Status: UNUSED - No component imports or calls this function (verified 2025-12-09)
// Replacement: Components use personal analytics API via direct fetch/axios calls
// Backend endpoint: /report/user/{user_id}/analysis also commented out
//
// Commented out to identify any hidden dependencies. If no errors after testing:
// - Delete this function
// - Delete backend endpoint /report/user/{user_id}/analysis
// - Delete deprecated backend endpoint /report/analysis/{email}
// ============================================================================
/*
export const getReportAnalysis = async () => {
  const id = await getIdentity();
  if (!id?.user_id) throw new Error('User not authenticated');
  try {
    const response = await apiClient.get(`/report/user/${id.user_id}/analysis`);
    return response.data;
  } catch (error) {
    console.error('Error getting report analysis:', error);
    throw error;
  }
};
*/

const buildAuthHeaders = async () => {
  try {
    const identity = await getIdentity();
    if (identity?.email) {
      return { 'X-User-Email': identity.email };
    }
  } catch (error) {
    console.warn('Unable to resolve identity for welcome overlay headers:', error);
  }
  return {};
};

export const getWelcomeOverlay = async (options = {}) => {
  const headers = await buildAuthHeaders();
  const params = new URLSearchParams();
  if (options.language) {
    params.set('language', options.language);
  }
  const url = params.toString() ? `/api/welcome-overlay/?${params.toString()}` : '/api/welcome-overlay/';
  try {
    const response = await apiClient.get(url, { headers });
    return response.data;
  } catch (error) {
    console.error('Error loading welcome overlay:', error);
    throw error;
  }
};

export const submitWelcomeOverlay = async (payload) => {
  const headers = await buildAuthHeaders();
  try {
    const response = await apiClient.post('/api/welcome-overlay/', payload, { headers });
    return response.data;
  } catch (error) {
    console.error('Error submitting welcome overlay:', error);
    throw error;
  }
};

// TODO: UNUSED Brain Log REST helpers (no matching backend routes). Keep commented until implemented or remove.
// export const getUserBrainLogs = async () => { /* ... */ };
// export const getSessionBrainLogs = async (sessionId) => { /* ... */ };
// export const getBrainLogStats = async () => { /* ... */ };

// Export the user helper for components that need current user info
export { getCurrentUser };
